import React, { useEffect, useRef } from "react";
import ReactSlider from "react-slider";
import { Button } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// Helper functions for logarithmic scale
// Converts linear slider position (0-100) to actual value using logarithmic scale
const logToValue = (position, min, max) => {
    // Handle edge case: position 0 should return exact min value (even if it's 0)
    if (position === 0) return min;
    if (position === 100) return max;

    // For log calculation, use offset if min is 0 or negative
    const logMin = min <= 0 ? 1 : min;
    const logMax = max <= 0 ? 1 : max;

    const minLog = Math.log10(logMin);
    const maxLog = Math.log10(logMax);
    const scale = (maxLog - minLog) / 100;
    const logValue = Math.pow(10, minLog + scale * position);

    // If original min was 0, offset the result
    return min <= 0 ? logValue + min - logMin : logValue;
};

// Converts actual value to linear slider position (0-100)
const valueToLog = (value, min, max) => {
    // Handle edge case: exact min/max values
    if (value <= min) return 0;
    if (value >= max) return 100;

    // For log calculation, use offset if min is 0 or negative
    const logMin = min <= 0 ? 1 : min;
    const logMax = max <= 0 ? 1 : max;
    const logValue = value <= 0 ? 1 : (min <= 0 ? value - min + logMin : value);

    const minLog = Math.log10(logMin);
    const maxLog = Math.log10(logMax);
    const scale = (maxLog - minLog) / 100;

    return Math.max(0, Math.min(100, (Math.log10(logValue) - minLog) / scale));
};

const ParcelFilters = ({
    // County selection
    selectedCounty,
    setSelectedCounty,
    countiesList = [],
    onCountyChange,

    // Display info
    currentZoom = 0,
    apiParcelsCount = 0,
    tileParcelsCount = 0,

    // Filter visibility states
    showPriceFilter, setShowPriceFilter,
    showSizeFilter, setShowSizeFilter,
    showFloodFilter, setShowFloodFilter,
    showZoneFilter, setShowZoneFilter,
    showZestimateFilter, setShowZestimateFilter,
    showSpreadFilter, setShowSpreadFilter,
    showMunicipalityFilter, setShowMunicipalityFilter,


    // Filter values
    priceRange, setPriceRange,
    sizeRange, setSizeRange,
    floodRiskFilter, setFloodRiskFilter,
    zoneCodeFilter, setZoneCodeFilter,
    zestimateRange, setZestimateRange,
    spreadRange, setSpreadRange,
    listedSaleFilter, setListedSaleFilter,
    vacantFilter, setVacantFilter,

    municipalityFilter,
    setMunicipalityFilter,
    municipalityOptions = ["All"],


    // Slider bounds
    sliderMinPrice, sliderMaxPrice,
    sliderMinSize, sliderMaxSize,
    sliderMinZestimate, sliderMaxZestimate,
    sliderMinSpread, sliderMaxSpread,

    // Options for dropdowns
    floodOptions = ["All"],
    zoneCodes = ["All"],
    listedSaleOptions = [],

    // Helper functions
    clearAllFilters,
    formatCurrency = (num) => num ? "$" + num.toLocaleString("en-US") : "N/A",

    // Callback for when slider drag ends
    onPriceRangeChangeComplete,
    onZestimateRangeChangeComplete,
    onSizeRangeChangeComplete,
    onSpreadRangeChangeComplete,
}) => {

    // const isMunicipalityActive = () =>
    // municipalityFilter && municipalityFilter !== "All";
    const isMunicipalityActive = () =>
        municipalityFilter !== "All";



    // Helper functions to check if filters are active
    const isPriceActive = () => {
        if (sliderMinPrice === undefined || sliderMaxPrice === undefined) return false;
        if (priceRange[0] === undefined || priceRange[1] === undefined) return false;
        // Use larger tolerance for log scale with large ranges - $5000 accounts for rounding
        const tolerance = 0.01;
        return Math.abs(priceRange[0] - sliderMinPrice) > tolerance ||
            Math.abs(priceRange[1] - sliderMaxPrice) > tolerance;
    };

    const isSizeActive = () => {
        if (sliderMinSize === undefined || sliderMaxSize === undefined) return false;
        if (sizeRange[0] === undefined || sizeRange[1] === undefined) return false;
        // Use small absolute tolerance for log scale - 0.01 acres is negligible
        const tolerance = 0.01;
        return Math.abs(sizeRange[0] - sliderMinSize) > tolerance ||
            Math.abs(sizeRange[1] - sliderMaxSize) > tolerance;
    };

    const isZestimateActive = () => {
        if (sliderMinZestimate === undefined || sliderMaxZestimate === undefined) return false;
        if (zestimateRange[0] === undefined || zestimateRange[1] === undefined) return false;
        // Use larger tolerance for log scale with large ranges - $5000 accounts for rounding
        const tolerance = 0.1;
        return Math.abs(zestimateRange[0] - sliderMinZestimate) > tolerance ||
            Math.abs(zestimateRange[1] - sliderMaxZestimate) > tolerance;
    };

    const isSpreadActive = () => {
        if (sliderMinSpread === undefined || sliderMaxSpread === undefined) return false;
        if (spreadRange[0] === undefined || spreadRange[1] === undefined) return false;
        // Use small absolute tolerance for log scale - 0.1% is negligible
        const tolerance = 0.1;
        return Math.abs(spreadRange[0] - sliderMinSpread) > tolerance ||
            Math.abs(spreadRange[1] - sliderMaxSpread) > tolerance;
    };

    const isFloodActive = () => floodRiskFilter && floodRiskFilter !== "All";
    const isZoneActive = () => zoneCodeFilter && zoneCodeFilter.length > 0;
    const isDefaultMunicipality =
        !municipalityFilter ||
        municipalityFilter.toLowerCase() === "all";

    // Ref for the filter container
    const filterContainerRef = useRef(null);

    // Click outside to close all filters
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterContainerRef.current && !filterContainerRef.current.contains(event.target)) {
                // Close all filter dropdowns
                setShowPriceFilter(false);
                setShowSizeFilter(false);
                setShowFloodFilter(false);
                setShowZoneFilter(false);
                setShowZestimateFilter(false);
                setShowSpreadFilter(false);
                setShowMunicipalityFilter(false);
            }
        };

        // Add event listener
        document.addEventListener("mousedown", handleClickOutside);

        // Cleanup
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []); // Empty dependency array - only set up once

    return (
        <div ref={filterContainerRef} style={{ position: "relative" }}>
            {/* Custom CSS for county dropdown */}
            <style>
                {`
                    .county-dropdown option {
                        border-radius: 6px;
                        padding: 8px 12px;
                    }
                    .county-dropdown option:hover {
                        background-color: #e8f5f3 !important;
                        color: #1c8f7d !important;
                    }
                `}
            </style>
            {/* All Filters in One Row */}
            <div
                className="filterOuter NCOuter"
                style={{
                    display: "flex",
                    gap: "10px",
                    padding: "10px 16px",
                    borderBottom: "1px solid #ddd",
                    position: "relative",
                    background: "#fff",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    overflowX: "auto",
                }}
            >
                {/* Municipality - Only show when county is selected */}
                {selectedCounty !== "all" && (
                    <Button
                        variant="contained"
                        color="primary filter-button"
                        endIcon={<ArrowDropDownIcon />}
                        onClick={() => {
                            setShowMunicipalityFilter(!showMunicipalityFilter);
                            setShowPriceFilter(false);
                            setShowSizeFilter(false);
                            setShowFloodFilter(false);
                            setShowZoneFilter(false);
                            setShowZestimateFilter(false);
                            setShowSpreadFilter(false);
                        }}
                        style={{
                            textTransform: "none",
                            background: isMunicipalityActive() ? "#1c8f7d" : "transparent",
                            color: isMunicipalityActive() ? "#fff" : "#333",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                            borderRadius: 6,
                        }}
                    >
                        {isDefaultMunicipality ? "Municipality" : municipalityFilter}
                    </Button>
                )}
                {selectedCounty !== "all" && (
                    <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "nowrap" }}>


                        {/* Land Value */}
                        <Button
                            variant="contained"
                            color="primary filter-button"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={() => {
                                setShowPriceFilter(!showPriceFilter);
                                setShowFloodFilter(false);
                                setShowSizeFilter(false);
                                setShowZoneFilter(false);
                                setShowZestimateFilter(false);
                                setShowSpreadFilter(false);
                                setShowMunicipalityFilter(false);
                            }}
                            style={{
                                textTransform: "none",
                                background: isPriceActive() ? "#1c8f7d" : "transparent",
                                color: isPriceActive() ? "#fff" : "#333",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                borderRadius: 6,
                            }}
                        >
                            Developed Value
                        </Button>

                        {/* Zestimate */}
                        <Button
                            color="primary filter-button"
                            variant="contained"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={() => {
                                setShowZestimateFilter(!showZestimateFilter);
                                setShowPriceFilter(false);
                                setShowSizeFilter(false);
                                setShowFloodFilter(false);
                                setShowZoneFilter(false);
                                setShowSpreadFilter(false);
                                setShowMunicipalityFilter(false);

                            }}
                            style={{
                                textTransform: "none",
                                background: isZestimateActive() ? "#1c8f7d" : "transparent",
                                color: isZestimateActive() ? "#fff" : "#333",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                borderRadius: 6,
                            }}
                        >
                            Price
                        </Button>

                        {/* Spread % */}
                        <Button
                            color="primary filter-button"
                            variant="contained"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={() => {
                                setShowSpreadFilter(!showSpreadFilter);
                                setShowPriceFilter(false);
                                setShowSizeFilter(false);
                                setShowFloodFilter(false);
                                setShowZoneFilter(false);
                                setShowZestimateFilter(false);
                            }}
                            style={{
                                textTransform: "none",
                                background: isSpreadActive() ? "#1c8f7d" : "transparent",
                                color: isSpreadActive() ? "#fff" : "#333",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                borderRadius: 6,
                            }}
                        >
                            Spread(%)
                        </Button>

                        {/* Size */}
                        <Button
                            color="primary filter-button"
                            variant="contained"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={() => {
                                setShowSizeFilter(!showSizeFilter);
                                setShowPriceFilter(false);
                                setShowFloodFilter(false);
                                setShowZoneFilter(false);
                                setShowZestimateFilter(false);
                                setShowSpreadFilter(false);
                                setShowMunicipalityFilter(false);

                            }}
                            style={{
                                textTransform: "none",
                                background: isSizeActive() ? "#1c8f7d" : "transparent",
                                color: isSizeActive() ? "#fff" : "#333",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                borderRadius: 6,
                            }}
                        >
                            Size
                        </Button>

                        {/* Zone Code */}
                        <Button
                            color="primary filter-button"
                            variant="contained"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={() => {
                                setShowZoneFilter(!showZoneFilter);
                                setShowPriceFilter(false);
                                setShowSizeFilter(false);
                                setShowFloodFilter(false);
                                setShowZestimateFilter(false);
                                setShowSpreadFilter(false);
                                setShowMunicipalityFilter(false);

                            }}
                            style={{
                                textTransform: "none",
                                background: isZoneActive() ? "#1c8f7d" : "transparent",
                                color: isZoneActive() ? "#fff" : "#333",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                borderRadius: 6,
                            }}
                        >
                            Zoning
                        </Button>

                        {/* Flood Risk */}
                        <Button
                            color="primary filter-button"
                            variant="contained"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={() => {
                                setShowFloodFilter(!showFloodFilter);
                                setShowPriceFilter(false);
                                setShowSizeFilter(false);
                                setShowZoneFilter(false);
                                setShowZestimateFilter(false);
                                setShowSpreadFilter(false);
                                setShowMunicipalityFilter(false);

                            }}
                            style={{
                                textTransform: "none",
                                background: isFloodActive() ? "#1c8f7d" : "transparent",
                                color: isFloodActive() ? "#fff" : "#333",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                borderRadius: 6,
                            }}
                        >
                            Flood Risk
                        </Button>

                        {/* Listed Sale - Checkbox */}
                        <label style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "13px",
                            cursor: "pointer",
                            padding: "6px 12px",
                            borderRadius: 6,
                            background: listedSaleFilter !== "All" ? "#e8f5f3" : "transparent",
                            border: "1px solid #ddd",
                            whiteSpace: "nowrap",
                        }}>
                            <input
                                type="checkbox"
                                checked={listedSaleFilter !== "All"}
                                onChange={(e) => {
                                    setListedSaleFilter(e.target.checked ? "Listed" : "All");
                                }}
                                style={{
                                    cursor: "pointer",
                                    width: "16px",
                                    height: "16px",
                                }}
                            />
                            <span style={{ fontWeight: 500 }}>Listed Sale</span>
                        </label>

                        {/* Vacant - Checkbox */}
                        <label style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "13px",
                            cursor: "pointer",
                            padding: "6px 12px",
                            borderRadius: 6,
                            background: vacantFilter !== "All" ? "#e8f5f3" : "transparent",
                            border: "1px solid #ddd",
                            whiteSpace: "nowrap",
                        }}>
                            <input
                                type="checkbox"
                                checked={vacantFilter !== "All"}
                                onChange={(e) => {
                                    setVacantFilter(e.target.checked ? "Vacant" : "All");
                                }}
                                style={{
                                    cursor: "pointer",
                                    width: "16px",
                                    height: "16px",
                                }}
                            />
                            <span style={{ fontWeight: 500 }}>Vacant</span>
                        </label>

                        {/* Info displays - Counts and Zoom */}
                        <div style={{
                            fontSize: "13px",
                            color: "#666",
                            display: "flex",
                            gap: "12px",
                            alignItems: "center",
                            marginLeft: "auto",
                            whiteSpace: "nowrap",
                            display: "none"
                        }}>
                            <span>
                                List: <strong style={{ marginLeft: 4 }}>{currentZoom >= 13 ? tileParcelsCount : apiParcelsCount}</strong>
                            </span>
                            <span>
                                Map: <strong style={{ marginLeft: 4 }}>{tileParcelsCount}</strong>
                            </span>
                            <div style={{
                                // background: "#f0f0f0",
                                // padding: "6px 12px",
                                // borderRadius: 6,
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#333",
                            }}>
                                Zoom: <strong>{currentZoom.toFixed(1)}</strong>
                            </div>
                        </div>

                        {/* Clear Filters button */}
                        <Button
                            color="primary filter-button"
                            variant="outlined"
                            onClick={clearAllFilters}
                            style={{
                                textTransform: "none",
                                borderColor: "#cfcfcf",
                                color: "#333",
                                fontSize: "13px",
                                padding: "6px 16px",
                                borderRadius: 6,
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Clear
                        </Button>
                    </div>
                )}
            </div>

            {showMunicipalityFilter && selectedCounty !== "all" && (
                <div
                    style={{
                        position: "absolute",
                        left: 28,
                        top: 46,
                        background: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        borderRadius: 6,
                        zIndex: 998,
                        minWidth: 180,
                        maxHeight: 300,
                        overflow: "auto",
                    }}
                >
                    {municipalityOptions.map(opt => (
                        <div
                            key={opt}
                            onClick={() => {
                                setMunicipalityFilter(opt);
                                setShowMunicipalityFilter(false);
                            }}
                            style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                background:
                                    municipalityFilter === opt ? "#1c8f7d" : "transparent",
                                color:
                                    municipalityFilter === opt ? "#fff" : "#000",
                            }}
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )}



            {/* LAND VALUE SLIDER */}
            {showPriceFilter && selectedCounty !== "all" && (
                <div
                    className="price-filter-container"
                    style={{
                        position: "absolute",
                        left: 180,
                        top: 46,
                        background: "#fff",
                        padding: "16px",
                        borderRadius: 6,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        zIndex: 998,
                        minWidth: 220,
                    }}
                >
                    <ReactSlider
                        className="horizontal-slider"
                        thumbClassName="example-thumb"
                        trackClassName="example-track"
                        value={[
                            valueToLog(priceRange[0], sliderMinPrice, sliderMaxPrice),
                            valueToLog(priceRange[1], sliderMinPrice, sliderMaxPrice)
                        ]}
                        onChange={(logValues) => {
                            const actualValues = [
                                Math.round(logToValue(logValues[0], sliderMinPrice, sliderMaxPrice)),
                                Math.round(logToValue(logValues[1], sliderMinPrice, sliderMaxPrice))
                            ];
                            console.log('💰 Price onChange:', { logValues, actualValues });
                            setPriceRange(actualValues);
                        }}
                        onAfterChange={(logValues) => {
                            const actualValues = [
                                Math.round(logToValue(logValues[0], sliderMinPrice, sliderMaxPrice)),
                                Math.round(logToValue(logValues[1], sliderMinPrice, sliderMaxPrice))
                            ];
                            console.log('✅ Price onAfterChange:', { logValues, actualValues });
                            onPriceRangeChangeComplete(actualValues);
                        }}
                        min={0}
                        max={100}
                        step={0.1}
                        pearling
                        minDistance={1}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                        <span>{formatCurrency(priceRange[0])}</span>
                        <span>{formatCurrency(priceRange[1])}</span>
                    </div>
                </div>
            )}

            {/* FLOOD RISK DROPDOWN */}
            {showFloodFilter && selectedCounty !== "all" && (
                <div
                    style={{
                        position: "absolute",
                        left: 820,
                        top: 46,
                        background: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        borderRadius: 6,
                        zIndex: 998,
                        minWidth: 150,
                        maxHeight: 300,
                        overflow: "auto",
                    }}
                >
                    {floodOptions.map((opt) => (
                        <div
                            key={opt}
                            onClick={() => {
                                setFloodRiskFilter(opt);
                                setShowFloodFilter(false);
                            }}
                            style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                background: floodRiskFilter === opt ? "#1c8f7d" : "transparent",
                                color: floodRiskFilter === opt ? "#fff" : "#000",
                            }}
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )}

            {/* SIZE SLIDER */}
            {showSizeFilter && selectedCounty !== "all" && (
                <div
                    className="price-filter-container"
                    style={{
                        position: "absolute",
                        left: 600,
                        top: 46,
                        background: "#fff",
                        padding: "16px",
                        borderRadius: 6,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        zIndex: 998,
                        minWidth: 220,
                    }}
                >
                    <ReactSlider
                        className="horizontal-slider"
                        thumbClassName="example-thumb"
                        trackClassName="example-track"
                        value={[
                            valueToLog(sizeRange[0], sliderMinSize, sliderMaxSize),
                            valueToLog(sizeRange[1], sliderMinSize, sliderMaxSize)
                        ]}
                        onChange={(logValues) => {
                            const actualValues = [
                                parseFloat(logToValue(logValues[0], sliderMinSize, sliderMaxSize).toFixed(2)),
                                parseFloat(logToValue(logValues[1], sliderMinSize, sliderMaxSize).toFixed(2))
                            ];
                            setSizeRange(actualValues);
                        }}
                        onAfterChange={(logValues) => {
                            const actualValues = [
                                parseFloat(logToValue(logValues[0], sliderMinSize, sliderMaxSize).toFixed(2)),
                                parseFloat(logToValue(logValues[1], sliderMinSize, sliderMaxSize).toFixed(2))
                            ];
                            onSizeRangeChangeComplete(actualValues);
                        }}
                        min={0}
                        max={100}
                        step={0.1}
                        pearling
                        minDistance={1}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                        <span>{sizeRange[0].toFixed(2)} acres</span>
                        <span>{sizeRange[1].toFixed(2)} acres</span>
                    </div>
                </div>
            )}

            {/* ZONE CODE DROPDOWN */}
            {showZoneFilter && selectedCounty !== "all" && (
                <div
                    style={{
                        position: "absolute",
                        left: 700,
                        top: 46,
                        background: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        borderRadius: 6,
                        zIndex: 998,
                        minWidth: 150,
                        maxHeight: 300,
                        overflow: "auto",
                        padding: "8px",
                    }}
                >
                    {zoneCodes.map((code) => (
                        <label
                            key={code}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "6px 8px",
                                cursor: "pointer",
                                borderRadius: 4,
                                background: zoneCodeFilter.includes(code) ? "#e8f5f3" : "transparent",
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={zoneCodeFilter.includes(code)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setZoneCodeFilter([...zoneCodeFilter, code]);
                                    } else {
                                        setZoneCodeFilter(zoneCodeFilter.filter(z => z !== code));
                                    }
                                }}
                                style={{ cursor: "pointer" }}
                            />
                            <span style={{ fontSize: "14px" }}>{code}</span>
                        </label>
                    ))}
                </div>
            )}

            {/* ZESTIMATE SLIDER */}
            {showZestimateFilter && selectedCounty !== "all" && (
                <div
                    className="price-filter-container"
                    style={{
                        position: "absolute",
                        left: 355,
                        top: 46,
                        background: "#fff",
                        padding: "16px",
                        borderRadius: 6,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        zIndex: 998,
                        minWidth: 220,
                    }}
                >
                    <ReactSlider
                        className="horizontal-slider"
                        thumbClassName="example-thumb"
                        trackClassName="example-track"
                        value={[
                            valueToLog(zestimateRange[0], sliderMinZestimate, sliderMaxZestimate),
                            valueToLog(zestimateRange[1], sliderMinZestimate, sliderMaxZestimate)
                        ]}
                        onChange={(logValues) => {
                            const actualValues = [
                                Math.round(logToValue(logValues[0], sliderMinZestimate, sliderMaxZestimate)),
                                Math.round(logToValue(logValues[1], sliderMinZestimate, sliderMaxZestimate))
                            ];
                            setZestimateRange(actualValues);
                        }}
                        onAfterChange={(logValues) => {
                            const actualValues = [
                                Math.round(logToValue(logValues[0], sliderMinZestimate, sliderMaxZestimate)),
                                Math.round(logToValue(logValues[1], sliderMinZestimate, sliderMaxZestimate))
                            ];
                            onZestimateRangeChangeComplete(actualValues);
                        }}
                        min={0}
                        max={100}
                        step={0.1}
                        pearling
                        minDistance={1}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                        <span>{formatCurrency(zestimateRange[0])}</span>
                        <span>{formatCurrency(zestimateRange[1])}</span>
                    </div>
                </div>
            )}

            {/* SPREAD % SLIDER */}
            {showSpreadFilter && selectedCounty !== "all" && (
                <div
                    className="price-filter-container"
                    style={{
                        position: "absolute",
                        left: 465,
                        top: 46,
                        background: "#fff",
                        padding: "16px",
                        borderRadius: 6,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        zIndex: 998,
                        minWidth: 220,
                    }}
                >
                    <ReactSlider
                        className="horizontal-slider"
                        thumbClassName="example-thumb"
                        trackClassName="example-track"
                        value={(() => {
                            // Handle negative values by offsetting to positive range
                            const offset = Math.abs(sliderMinSpread) + 1;
                            const adjustedMin = sliderMinSpread + offset;
                            const adjustedMax = sliderMaxSpread + offset;
                            const adjustedValue0 = spreadRange[0] + offset;
                            const adjustedValue1 = spreadRange[1] + offset;
                            return [
                                valueToLog(adjustedValue0, adjustedMin, adjustedMax),
                                valueToLog(adjustedValue1, adjustedMin, adjustedMax)
                            ];
                        })()}
                        onChange={(logValues) => {
                            const offset = Math.abs(sliderMinSpread) + 1;
                            const adjustedMin = sliderMinSpread + offset;
                            const adjustedMax = sliderMaxSpread + offset;
                            const actualValues = [
                                parseFloat(logToValue(logValues[0], adjustedMin, adjustedMax).toFixed(1)) - offset,
                                parseFloat(logToValue(logValues[1], adjustedMin, adjustedMax).toFixed(1)) - offset
                            ];
                            setSpreadRange(actualValues);
                        }}
                        onAfterChange={(logValues) => {
                            const offset = Math.abs(sliderMinSpread) + 1;
                            const adjustedMin = sliderMinSpread + offset;
                            const adjustedMax = sliderMaxSpread + offset;
                            const actualValues = [
                                parseFloat(logToValue(logValues[0], adjustedMin, adjustedMax).toFixed(1)) - offset,
                                parseFloat(logToValue(logValues[1], adjustedMin, adjustedMax).toFixed(1)) - offset
                            ];
                            onSpreadRangeChangeComplete(actualValues);
                        }}
                        min={0}
                        max={100}
                        step={0.1}
                        pearling
                        minDistance={1}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                        <span>{spreadRange[0].toFixed(1)}%</span>
                        <span>{spreadRange[1].toFixed(1)}%</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParcelFilters;
